/**
 * Copy one wall's layout to other walls of the room.
 *
 * The sections go across at the same heights from the floor, so bands (a
 * SlatWall strip, a two-tone split) line up all the way round. A section that
 * spans its wall's full length spans the target's; one that does not keeps its
 * x and width, and is left off if it would run past the target's end. On a
 * target of a different height the TOP-MOST section is stretched or trimmed to
 * the target's ceiling (anything wholly above it is left off), so a SlatWall
 * band at 3′ stays at 3′ whatever the wall height.
 *
 * A target keeps its own windows and doors — they are the room's, not the
 * layout's. Windows, doors and garage doors come along only when asked for,
 * kind by kind: that kind on the target is then replaced by the source's, as
 * new openings of their own (a copy on a wall two rooms share is not linked to
 * anything). One that would not fit the target — off its end, over its top,
 * colliding with an opening that stays — is left off.
 *
 * The target's sections are replaced. The copy is one edit, so it undoes as one.
 */

import { Opening, OpeningKind, RoomSurfaces, Section, Surface } from '../../types/sections';
import { EditResult } from './edits';
import { nextOpeningId } from './openings';
import { checkOpeningPlacement } from './validation';
import { validateSurface } from './validation';

export interface CopyLayoutOptions {
  /** Which kinds of opening to bring along (none by default). */
  openings?: Partial<Record<Exclude<OpeningKind, 'passage'>, boolean>>;
  /** Safe margin for openings (feet). */
  margin?: number;
}

const EPS = 1e-6;
const nextId = (surface: Surface, type: Section['productType']) => {
  let n = 1;
  const ids = new Set(surface.sections.map((s) => s.id));
  while (ids.has(`${surface.id}-${type.toLowerCase()}-${n}`)) n++;
  return `${surface.id}-${type.toLowerCase()}-${n}`;
};

/** One wall's sections, laid onto another wall. */
export function copySectionsTo(from: Surface, to: Surface): Section[] {
  const out: Section[] = [];
  const topMost = from.sections.reduce<Section | null>((best, s) => (!best || s.y + s.height > best.y + best.height ? s : best), null);
  const scratch: Surface = { ...to, sections: [] };
  for (const s of from.sections) {
    const fullWidth = s.x <= EPS && Math.abs(s.x + s.width - from.width) <= EPS;
    const x = fullWidth ? 0 : s.x;
    const width = fullWidth ? to.width : s.width;
    if (x + width > to.width + EPS) continue;         // runs past the target's end
    let y = s.y;
    let height = s.height;
    if (s === topMost) height = to.height - y;        // the top section meets the target's ceiling
    if (y >= to.height - EPS || height <= EPS) continue; // wholly above the target
    if (y + height > to.height + EPS) height = to.height - y;
    const copy: Section = { ...s, id: nextId(scratch, s.productType), x, y, width, height };
    scratch.sections.push(copy);
    out.push(copy);
  }
  return out;
}

/** The chosen kinds of a wall's openings, laid onto another wall where they fit. */
export function copyOpeningsTo(from: Surface, to: Surface, kinds: CopyLayoutOptions['openings'], margin: number): Opening[] {
  const out: Opening[] = [];
  let scratch: Surface = { ...to, openings: [] };
  for (const o of from.openings ?? []) {
    if (o.kind === 'passage' || !kinds?.[o.kind]) continue;
    const rect = { x: o.x, y: o.y, width: o.width, height: o.height };
    if (checkOpeningPlacement(scratch, rect, o, { margin })) continue;
    const copy: Opening = { ...o, id: nextOpeningId(scratch, o.kind), link: undefined };
    scratch = { ...scratch, openings: [...(scratch.openings ?? []), copy] };
    out.push(copy);
  }
  return out;
}

/**
 * Copy the layout of wall `fromId` onto the walls `toIds` (any other walls of
 * the room). Refused only when a wall is not found; a target that ends up
 * breaking a placement rule keeps its sections but drops the offending
 * openings, so the result is always a sound room.
 */
export function copyLayout(room: RoomSurfaces, fromId: string, toIds: string[], options: CopyLayoutOptions = {}): EditResult {
  const from = room.walls.find((w) => w.id === fromId);
  if (!from) return { ok: false, reason: 'not-found' };
  const targets = toIds.filter((id) => id !== fromId);
  if (targets.some((id) => !room.walls.some((w) => w.id === id))) return { ok: false, reason: 'not-found' };
  const margin = options.margin ?? 0;
  const walls = room.walls.map((wall) => {
    if (!targets.includes(wall.id)) return wall;
    // The target keeps its openings, except the kinds being copied over (those
    // are replaced by the source's). A `passage` (a removed shared wall) always stays.
    const keep = (wall.openings ?? []).filter((o) => o.kind === 'passage' || !options.openings?.[o.kind]);
    const sections = copySectionsTo(from, wall);
    let next: Surface = { ...wall, sections, openings: [...keep, ...copyOpeningsTo(from, { ...wall, openings: keep }, options.openings, margin)] };
    // Belt and braces: if the copy breaks a rule (an opening over a section edge
    // it may not cross, say), drop the copied openings one by one until it holds.
    let issues = validateSurface(next);
    while (issues.length > 0 && (next.openings?.length ?? 0) > keep.length) {
      next = { ...next, openings: next.openings!.slice(0, -1) };
      issues = validateSurface(next);
    }
    if (next.openings && next.openings.length === 0) delete next.openings;
    return next;
  });
  return { ok: true, room: { ...room, walls } };
}
